import {
  assignmentsFromSummary,
  buildTimeline,
  DEFAULT_QUARTER_BPM,
  type ParsedScore,
  type ScoreSummary,
  timelineRange,
  type VoicePart,
  VOICE_PARTS,
} from '@corocanto/scoring';
import { useQueries } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { type Channel, channelGains, DEFAULT_MIXER, type Mixer as MixerState, toggleMute } from '../audio/mix';
import { metronomeClicks, planPlayback } from '../audio/plan';
import {
  applyFocus,
  markAt,
  matchingFocus,
  partFocuses,
  rehearsalMarks,
  SHORTCUT_CHANNELS,
  startingNotes,
  timelineIndexOfMeasure,
  timelineIndexOfNumber,
} from '../audio/rehearsal';
import { usePracticePlayer } from '../audio/usePracticePlayer';
import { useEvent, useMyEvent } from '../api/events';
import { songFileQuery } from '../api/songs';
import { useParsedSong } from '../api/useParsedSong';
import { isDirector, useMembership } from '../auth/session';
import { ErrorView, Loading } from '../components/Guards';
import { Mixer } from '../components/Mixer';
import { FocusIcon } from '../components/ModeIcon';
import { canChooseOutput, setOutputDevice } from '../audio/context';
import { useRehearsalSpeaker } from '../audio/speakers';
import { PlaybackToggles } from '../components/PlaybackToggles';
import { useBusy } from '../maintenance/busy';
import { PresetPicker } from '../components/PresetPicker';
import { ScoreView } from '../components/ScoreView';
import { isShortcutKey, releaseButtonFocus } from '../lib/shortcuts';
import { TempoCounter } from '../components/TempoCounter';
import { Transport } from '../components/Transport';
import controls from '../styles/controls.module.css';
import ui from '../styles/ui.module.css';
import styles from './RehearsalPage.module.css';

/** The tempo goes from half to one and a half times the marked tempo, as on the practice page. */
const TEMPO_MIN_FACTOR = 0.5;
const TEMPO_MAX_FACTOR = 1.5;
/** Wait this long after the last tempo change before replanning playback. */
const TEMPO_SETTLE_MS = 350;
const STARTING_NOTE_SEC = 2;
const TRANSPOSE_LIMIT = 6;

interface RehearsalSong {
  songId: string;
  title: string;
}

/**
 * Rehearsal mode (wireframe 07): plays an event's repertoire through the room's speakers while the
 * director conducts. Open to every member and the director; playback only, no microphone, no grades.
 */
export function RehearsalPage() {
  const { eventId } = useParams();
  const membership = useMembership();
  const director = isDirector(membership);
  // The director reads the event from the director's endpoint, everyone else from their own.
  const directorEvent = useEvent(director ? membership?.choirId : undefined, director ? eventId : undefined);
  const memberEvent = useMyEvent(director ? undefined : membership?.choirId, director ? undefined : eventId);
  const source = director ? directorEvent : memberEvent;
  if (!membership) return null;
  if (source.isPending) return <Loading />;
  if (source.isError || !source.data) return <ErrorView onRetry={() => void source.refetch()} />;
  const event = director ? directorEvent.data : memberEvent.data?.event;
  const program = director ? (directorEvent.data?.program ?? []) : (memberEvent.data?.program ?? []);
  const songs: RehearsalSong[] = program.flatMap((i) => (i.kind === 'SONG' && i.songId ? [{ songId: i.songId, title: i.songTitle ?? '' }] : []));
  return <Rehearsal choirId={membership.choirId} referenceA4={membership.referencePitchHz} eventId={eventId ?? ''} eventName={event?.name ?? ''} songs={songs} />;
}

function Rehearsal({ choirId, referenceA4, eventId, eventName, songs }: { choirId: string; referenceA4: number; eventId: string; eventName: string; songs: RehearsalSong[] }) {
  const { t } = useTranslation();
  const [songId, setSongId] = useState<string | null>(songs[0]?.songId ?? null);
  // Load every song of the programme now, so switching songs in the room needs no network.
  const files = useQueries({ queries: songs.map((s) => songFileQuery(choirId, s.songId)) });
  const loaded = files.filter((f) => f.data).length;
  const { song, parsed, isPending, isError, refetch } = useParsedSong(choirId, songId ?? undefined);

  // Play through the room's speakers (chosen in the header's audio menu), not the headset check's output.
  const speaker = useRehearsalSpeaker();
  useEffect(() => {
    if (canChooseOutput()) void setOutputDevice(speaker.deviceId);
  }, [speaker.deviceId]);

  // The parts and the tuning stay as they are when the song changes.
  const [desk, setDesk] = useState<MixerState>(DEFAULT_MIXER);
  const [tuning, setTuning] = useState(referenceA4);

  return (
    <div className={`${ui.page} ${styles.page}`} data-testid="rehearsal-page" data-output={speaker.deviceId || 'default'}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <span className={styles.modeBadge}>{t('rehearsal.title')}</span>
          <h1>{eventName}</h1>
          <span className={ui.muted}>
            {t('rehearsal.program')} · <span data-testid="songs-loaded">{t('rehearsal.loaded', { loaded, total: songs.length })}</span>
          </span>
        </div>
        <Link to={`/events/${eventId}`}>{t('rehearsal.exit')}</Link>
      </div>
      {songs.length === 0 ? (
        <section className={ui.card}>
          <p className={ui.empty}>{t('rehearsal.noSongs')}</p>
        </section>
      ) : (
        <div className={styles.stack}>
          <nav className={`${ui.card} ${styles.repertoire}`} aria-label={t('rehearsal.repertoire')}>
            <h2 className={ui.cardTitle}>{t('rehearsal.repertoire')}</h2>
            <ol>
              {songs.map((s, i) => (
                <li key={s.songId}>
                  <button
                    type="button"
                    className={s.songId === songId ? `${styles.songButton} ${styles.songOn}` : styles.songButton}
                    aria-current={s.songId === songId}
                    data-testid="rehearsal-song"
                    onClick={() => setSongId(s.songId)}
                  >
                    <span className={styles.songNumber}>{i + 1}</span>
                    <span className={styles.songTitle} title={s.title}>
                      {s.title}
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          {isPending ? (
            <Loading />
          ) : isError || !parsed || !song ? (
            <ErrorView onRetry={() => void refetch()} />
          ) : (
            <SongDesk
              key={song.id}
              title={song.title}
              summary={song.summary}
              score={parsed.score}
              xml={parsed.xml}
              desk={desk}
              setDesk={setDesk}
              tuning={tuning}
              setTuning={setTuning}
              referenceA4={referenceA4}
            />
          )}
        </div>
      )}
    </div>
  );
}

interface SongDeskProps {
  title: string;
  summary: ScoreSummary;
  score: ParsedScore;
  xml: string;
  desk: MixerState;
  setDesk: (update: (d: MixerState) => MixerState) => void;
  tuning: number;
  setTuning: (hz: number) => void;
  referenceA4: number;
}

/** One song in rehearsal: the controls panel (transport, parts, tempo, bars, mixer), the score, and giving the pitch. */
function SongDesk({ title, summary, score, xml, desk, setDesk, tuning, setTuning, referenceA4 }: SongDeskProps) {
  const { t } = useTranslation();
  const assignments = useMemo(() => assignmentsFromSummary(summary), [summary]);
  const songParts = useMemo(() => VOICE_PARTS.filter((p) => summary.parts.some((s) => s.voicePart === p)), [summary]);
  const hasAccompaniment = summary.parts.some((p) => p.role === 'accompaniment');
  const channels: Channel[] = useMemo(() => [...songParts, ...(hasAccompaniment ? (['acc'] as const) : [])], [songParts, hasAccompaniment]);

  const markedBpm = summary.tempo?.quarterBpm ?? DEFAULT_QUARTER_BPM;
  const minBpm = Math.ceil(markedBpm * TEMPO_MIN_FACTOR);
  const maxBpm = Math.floor(markedBpm * TEMPO_MAX_FACTOR);
  const [bpm, setBpm] = useState(() => Math.round(markedBpm));
  const [effectiveBpm, setEffectiveBpm] = useState(bpm);
  useEffect(() => {
    const timer = window.setTimeout(() => setEffectiveBpm(bpm), TEMPO_SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [bpm]);
  const tempoFactor = effectiveBpm / markedBpm;
  const timeline = useMemo(() => buildTimeline(score, { assignments, tempoFactor }), [score, assignments, tempoFactor]);
  const lastTimeline = timeline.measures.length - 1;

  const [transpose, setTranspose] = useState(0);
  const [metronome, setMetronome] = useState(false);
  const [countIn, setCountIn] = useState(true);
  const [loop, setLoop] = useState(false);
  const [loopFrom, setLoopFrom] = useState(0);
  const [loopTo, setLoopTo] = useState(score.measures.length - 1);
  /** The current bar, in playback order: where Play starts, and the bar being played. */
  const [at, setAt] = useState(0);
  const [goTo, setGoTo] = useState('');
  const [goToError, setGoToError] = useState<string | null>(null);
  const goToRef = useRef<HTMLInputElement>(null);

  const player = usePracticePlayer(timeline);
  const { view } = player;
  const playing = view.phase !== 'stopped';
  // A deploy's maintenance notice waits until playback stops.
  useBusy(playing);
  const ready = view.status === 'ready';

  useEffect(() => {
    if (ready) player.setGains(channelGains(desk));
  }, [ready, desk, player.setGains]);
  useEffect(() => {
    player.setLoop(loop);
  }, [loop, player.setLoop]);
  // While playing, the current bar follows playback.
  useEffect(() => {
    if (view.position) setAt(view.position.timelineMeasureIndex);
  }, [view.position]);

  const loopRange = useMemo(() => timelineRange(timeline, loopFrom, loopTo), [timeline, loopFrom, loopTo]);

  const start = useCallback(
    (from: number, options: { countIn?: boolean } = {}) => {
      // A restart in the middle of playback (new bar, tempo or tuning) has no count-in.
      const range = loop && loopRange ? loopRange : { first: 0, last: lastTimeline };
      const startMeasure = Math.min(Math.max(from, range.first), range.last);
      const plan = planPlayback(timeline, score.measures, {
        firstMeasure: range.first,
        lastMeasure: range.last,
        startMeasure,
        countInBars: (options.countIn ?? true) && countIn ? 1 : 0,
      });
      setAt(startMeasure);
      void player.play(plan, {
        loop,
        referenceA4: tuning,
        transpose,
        metronome: metronome ? metronomeClicks(timeline, score.measures, range.first, range.last) : null,
        onEnded: () => setAt(range.first),
      });
    },
    [loop, loopRange, lastTimeline, timeline, score.measures, countIn, player.play, tuning, transpose, metronome],
  );

  // A new tempo, tuning, transposition or metronome while playing: carry on from the current bar, no count-in.
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const atRef = useRef(at);
  atRef.current = at;
  const settings = `${timeline.tempoFactor}|${tuning}|${transpose}|${metronome}`;
  const previousSettings = useRef(settings);
  useEffect(() => {
    if (previousSettings.current === settings) return;
    previousSettings.current = settings;
    if (playingRef.current) start(atRef.current, { countIn: false });
    // Only a change of these settings should trigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  const moveTo = useCallback(
    (index: number) => {
      setGoToError(null);
      const next = Math.min(Math.max(index, 0), lastTimeline);
      if (playingRef.current) start(next, { countIn: false });
      else setAt(next);
    },
    [lastTimeline, start],
  );
  const playPause = useCallback(() => {
    if (!ready) return;
    if (playingRef.current) player.stop();
    else start(atRef.current);
  }, [ready, player.stop, start]);

  const goToBar = (value: string) => {
    const index = timelineIndexOfNumber(timeline, value);
    if (index === null) {
      setGoToError(t('rehearsal.noSuchBar', { bar: value.trim() }));
      return;
    }
    setGoToError(null);
    setGoTo('');
    moveTo(index);
  };

  const toggleChannel = useCallback(
    (ch: Channel) => {
      if (!channels.includes(ch)) return;
      setDesk((d) => toggleMute(d, ch));
    },
    [channels, setDesk],
  );

  // Keyboard shortcuts, except while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isShortcutKey(e)) return;
      if (e.key === ' ') {
        e.preventDefault();
        releaseButtonFocus();
        playPause();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        moveTo(atRef.current - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        moveTo(atRef.current + 1);
      } else if (e.key === 'g' || e.key === 'G') {
        e.preventDefault();
        goToRef.current?.focus();
      } else if (/^[1-5]$/.test(e.key)) {
        const ch = SHORTCUT_CHANNELS[Number(e.key) - 1];
        if (ch) toggleChannel(ch);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [playPause, moveTo, toggleChannel]);

  const current = timeline.measures[at] ?? timeline.measures[0];
  const mark = markAt(timeline, score.measures, at);
  const marks = useMemo(() => rehearsalMarks(score.measures), [score.measures]);
  const focus = matchingFocus(desk, songParts, hasAccompaniment);
  const starting = useMemo(() => startingNotes(timeline, at, songParts), [timeline, at, songParts]);
  const playStarting = (parts: readonly VoicePart[]) => {
    const midis = parts.flatMap((p) => (starting[p] ? [starting[p].midi + transpose] : []));
    if (midis.length > 0) void player.playChord(midis, STARTING_NOTE_SEC, tuning);
  };
  const tuningOptions = [...new Set([440, 442, referenceA4])].sort((a, b) => a - b);

  const cursor = view.position
    ? { measureIndex: view.position.measureIndex, beatOffset: view.position.beatOffset }
    : current
      ? { measureIndex: current.measureIndex, beatOffset: 0 }
      : null;
  const selection = loop ? { from: loopFrom, to: loopTo } : current ? { from: current.measureIndex, to: current.measureIndex } : null;
  const bar = current?.measureNumber ?? '';
  const status =
    view.status === 'loading'
      ? t('practice.samplesLoading', { loaded: view.samplesLoaded, total: view.samplesTotal })
      : view.phase === 'countIn' && view.countInBeat > 0
        ? t('practice.countingIn', { beat: view.countInBeat })
        : mark
          ? t('rehearsal.statusMark', { bar, count: score.measures.length, mark })
          : t('rehearsal.status', { bar, count: score.measures.length });

  return (
    <>
      <section className={`${ui.card} ${controls.panel}`}>
        <div className={controls.rows}>
          <div className={controls.row}>
            <Transport
              ref={player.diagnosticsRef}
              data-testid="rehearsal-transport"
              data-status={view.status}
              data-phase={view.phase}
              data-bar={bar}
              data-timeline-measure={at}
              data-bpm={effectiveBpm}
              data-tuning={tuning}
              data-transpose={transpose}
              data-metronome={metronome ? 'on' : 'off'}
              status={status}
              live={false}
              playing={playing}
              canPlay={ready}
              playHint={view.status === 'loading' ? status : t('practice.play')}
              onPlay={playPause}
              onStop={playPause}
              rewind={{ label: t('practice.backToStart'), onRewind: () => moveTo(loop && loopRange ? loopRange.first : 0) }}
            />
            <PresetPicker
              label={t('rehearsal.parts')}
              labelId="parts-label"
              current={focus}
              currentLabel={t(`rehearsal.focus.${focus ?? 'custom'}`)}
              options={partFocuses(songParts).map((f) => ({
                id: f,
                label: t(`rehearsal.focus.${f}`),
                icon: <FocusIcon focus={f} />,
              }))}
              onChoose={(f) => setDesk((d) => applyFocus(f, d))}
            />
            <div className={styles.tempo} data-testid="rehearsal-tempo">
              <TempoCounter bpm={bpm} marked={markedBpm} min={minBpm} max={maxBpm} onChange={setBpm} />
              <PlaybackToggles metronome={metronome} countIn={countIn} onMetronome={setMetronome} onCountIn={setCountIn} />
            </div>
          </div>
          <div className={controls.row}>
            <form
              className={styles.goTo}
              onSubmit={(e) => {
                e.preventDefault();
                goToBar(goTo);
              }}
            >
              <label className={controls.group}>
                <span className={ui.label}>{t('rehearsal.goToBar')}</span>
                <input
                  ref={goToRef}
                  className={`${ui.input} ${styles.barInput}`}
                  value={goTo}
                  inputMode="numeric"
                  onChange={(e) => {
                    setGoTo(e.target.value);
                    setGoToError(null);
                  }}
                />
              </label>
              <button type="submit" className={ui.button}>
                {t('rehearsal.go')}
              </button>
              {goToError && (
                <span className={styles.error} role="alert">
                  {goToError}
                </span>
              )}
            </form>
            {marks.length > 0 && (
              <div className={controls.group}>
                <span className={ui.label}>{t('rehearsal.marks')}</span>
                <div className={styles.marks}>
                  {marks.map((m) => (
                    <button
                      key={`${m.label}-${m.measureIndex}`}
                      type="button"
                      className={mark === m.label ? `${styles.markButton} ${styles.markOn}` : styles.markButton}
                      aria-label={t('rehearsal.mark', { mark: m.label })}
                      onClick={() => {
                        const index = timelineIndexOfMeasure(timeline, m.measureIndex);
                        if (index !== null) moveTo(index);
                      }}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className={controls.rangeGroup}>
              <label className={controls.group}>
                <span className={ui.label}>{t('practice.fromBar')}</span>
                <select
                  className={ui.select}
                  value={loopFrom}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setLoopFrom(v);
                    if (loopTo < v) setLoopTo(v);
                  }}
                >
                  {summary.measureNumbers.map((n, i) => (
                    <option key={i} value={i}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className={controls.group}>
                <span className={ui.label}>{t('practice.toBar')}</span>
                <select
                  className={ui.select}
                  value={loopTo}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setLoopTo(v);
                    if (loopFrom > v) setLoopFrom(v);
                  }}
                >
                  {summary.measureNumbers.map((n, i) => (
                    <option key={i} value={i}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <label className={controls.loop}>
                <input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} />
                {t('rehearsal.loop')}
              </label>
            </div>
          </div>
        </div>
        <div data-testid="rehearsal-parts">
          <Mixer
            channels={channels}
            disabled={hasAccompaniment ? [] : ['acc']}
            mixer={desk}
            myPart={null}
            onChange={(ch, setting) => setDesk((d) => ({ ...d, [ch]: setting }))}
          />
        </div>
      </section>
      {view.status === 'error' && <div className={`${ui.notice} ${ui.noticeError}`}>{t('practice.samplesError')}</div>}

      <section className={`${ui.card} ${styles.scoreCard}`}>
        {/* The song is highlighted in the repertoire row; the score gets all the height. */}
        <h2 className={ui.visuallyHidden} data-testid="rehearsal-title">
          {title}
        </h2>
        <ScoreView
          xml={xml}
          emphasisPartIds={[]}
          cursor={cursor}
          selection={selection}
          onBarClick={(measureIndex) => {
            // Selects the bar, like the arrow keys: playback moves there only if it is playing.
            const index = timelineIndexOfMeasure(timeline, measureIndex);
            if (index !== null) moveTo(index);
          }}
          measureNumbers={summary.measureNumbers}
          horizontal
        />
      </section>

      <section className={`${ui.card} ${styles.pitch}`} data-testid="rehearsal-pitch">
        <div className={controls.group}>
          <span className={ui.label}>{t('rehearsal.startingNotes', { bar })}</span>
          <div className={styles.noteButtons}>
            {songParts.map((p) => (
              <button
                key={p}
                type="button"
                className={styles.noteButton}
                disabled={!ready || !starting[p]}
                aria-label={t('rehearsal.playStart', { part: t(`parts.${p}`) })}
                onClick={() => playStarting([p])}
              >
                {t(`partLetters.${p}`)}
              </button>
            ))}
            <button type="button" className={`${styles.noteButton} ${styles.chordButton}`} disabled={!ready} onClick={() => playStarting(songParts)}>
              {t('rehearsal.chord')}
            </button>
          </div>
        </div>
        <label className={controls.group}>
          <span className={ui.label}>{t('rehearsal.tuning')}</span>
          <select className={ui.select} value={tuning} onChange={(e) => setTuning(Number(e.target.value))}>
            {tuningOptions.map((hz) => (
              <option key={hz} value={hz}>
                A = {hz} Hz
              </option>
            ))}
          </select>
        </label>
        <div className={controls.group}>
          <span className={ui.label}>{t('rehearsal.transpose')}</span>
          <div className={styles.transpose}>
            <button type="button" className={ui.button} aria-label={t('rehearsal.down')} disabled={transpose <= -TRANSPOSE_LIMIT} onClick={() => setTranspose((v) => v - 1)}>
              −
            </button>
            <span className={styles.transposeValue} data-testid="transpose-value">
              {transpose > 0 ? `+${transpose}` : transpose}
            </span>
            <button type="button" className={ui.button} aria-label={t('rehearsal.up')} disabled={transpose >= TRANSPOSE_LIMIT} onClick={() => setTranspose((v) => v + 1)}>
              +
            </button>
          </div>
        </div>
        {tuning !== referenceA4 && <p className={`${ui.muted} ${styles.tuningNote}`}>{t('rehearsal.tuningNote', { hz: referenceA4 })}</p>}
      </section>
      <p className={`${ui.muted} ${styles.shortcuts}`}>
        {t('rehearsal.clickToSelect')} · {t('rehearsal.shortcuts')}
      </p>
    </>
  );
}
